import { BillingRecordPicker } from './BillingRecordPicker'
import { useAuth } from '../../contexts/AuthContext'
import { useQuery } from '@tanstack/react-query'
import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  Button,
  Input,
  Select,
  Option,
  Textarea,
  Card,
  CardContent,
  Stack,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  Alert,
  Accordion, AccordionSummary, AccordionDetails,
  CircularProgress
} from '@mui/joy'
import { FloppyDisk as Save, XCircle as Cancel } from '@phosphor-icons/react'
import Checkbox from '@mui/joy/Checkbox'
import { format } from 'date-fns'
import type { EditableLineItem, TaxRate } from '../../types/billing'
import type { BackendInvoice, BackendEstimate } from '../../types/billing-backend'
import type { Contact, Opportunity } from '../../types'
import type { Job } from '../../types'
import LineItemsEditor from './LineItemsEditor'
import api from '../../lib/api'
import { documentTemplatesApi } from '../../lib/billingApi'
import { PAYMENT_TERMS, dueDateFromTerms } from './paymentTerms'

interface DocumentFormProps {
  document?: BackendInvoice | BackendEstimate
  documentType: 'invoice' | 'estimate'
  customers: Contact[]
  jobs: Job[]
  opportunities?: Opportunity[]
  taxRates: TaxRate[]
  onSubmit: (data: any) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
  /** When true (paid invoices), lock customer/amounts/line items; allow job + notes/terms/dates. */
  lockFinancialFields?: boolean
  initialCustomerId?: number
  /** Job this document is being raised for (arrives as `?job=` from Job Detail). */
  initialJobId?: number
  /** Opportunity this document is being raised for (`?opportunity=`). */
  initialOpportunityId?: number
  initialLineItems?: Array<{
    description: string
    notes?: string
    quantity: number
    unit_price: number
    item_type: string
    is_taxable: boolean
    tax_rate: number | null
    total: number
    sort_order: number
    is_bold?: boolean
    material_spec_id?: number
    calculation_data?: any
  }>
}

export default function DocumentForm({
  document,
  documentType,
  customers,
  jobs,
  opportunities = [],
  taxRates,
  onSubmit,
  onCancel,
  isLoading = false,
  lockFinancialFields = false,
  initialCustomerId,
  initialJobId,
  initialOpportunityId,
  initialLineItems
}: DocumentFormProps) {
  const [organizationSettings, setOrganizationSettings] = useState<{
    target_overhead: number | null
    target_profit: number | null
    default_tax_rate: number | null
  }>({ target_overhead: null, target_profit: null, default_tax_rate: null })

  const initialDocumentDate = document?.document_date || format(new Date(), 'yyyy-MM-dd')
  const initialPaymentTerms = document?.payment_terms || 'net_30'
  // Once the user picks terms themselves, a slow default-template fetch must
  // not land on top of their choice.
  const paymentTermsTouched = useRef(false)
  // The customer is copied from the URL's job/opportunity exactly once. Without
  // this the copy fires again the moment the user clears the customer, and the
  // field can never be emptied.
  const associationCustomerSeeded = useRef(false)

  const [formData, setFormData] = useState({
    customer: document?.customer || initialCustomerId || null,
    job: document?.job || initialJobId || null,
    opportunity: (document as BackendEstimate)?.opportunity || initialOpportunityId || null,
    association_type:
      document?.job || initialJobId
        ? 'job'
        : (document as BackendEstimate)?.opportunity || initialOpportunityId
          ? 'opportunity'
          : 'none',
    document_date: initialDocumentDate,
    // A blank due date makes the invoice unable to age (is_overdue short-circuits
    // on null), so a new document starts on the date its terms imply. Still editable.
    due_date: document ? document.due_date || '' : dueDateFromTerms(initialDocumentDate, initialPaymentTerms),
    payment_terms: initialPaymentTerms,
    custom_payment_terms: document?.custom_payment_terms || '',
    terms_and_conditions: document?.terms_and_conditions || '',
    notes: document?.notes || '',
    internal_notes: document?.internal_notes || '',
    valid_until: (document as BackendEstimate)?.valid_until || '',
    is_recurring: (document as BackendInvoice)?.is_recurring || false,
    recurring_frequency: (document as BackendInvoice)?.recurring_frequency || 'monthly',
    late_fee_percentage: parseFloat((document as BackendInvoice)?.late_fee_percentage || '0'),
    include_overhead: document?.include_overhead || false,
    overhead_percentage: document?.overhead_percentage ? parseFloat(document.overhead_percentage) : null,
    include_profit: document?.include_profit || false,
    profit_percentage: document?.profit_percentage ? parseFloat(document.profit_percentage) : null
  })

  const [lineItems, setLineItems] = useState<EditableLineItem[]>(
    document?.line_items || initialLineItems || []
  )
  const [totals, setTotals] = useState({
    subtotal: parseFloat(document?.subtotal || '0'),
    overhead_amount: parseFloat(document?.overhead_amount || '0'),
    profit_amount: parseFloat(document?.profit_amount || '0'),
    tax_amount: parseFloat(document?.tax_amount || '0'),
    discount_amount: parseFloat(document?.discount_amount || '0'),
    total_amount: parseFloat(document?.total_amount || '0')
  })
  const [discountAmount, setDiscountAmount] = useState(
    parseFloat(document?.discount_amount || '0')
  )
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSaving, setIsSaving] = useState(false)

  const {user} = useAuth()
  const draftKey = `billing-draft:${user?.organization}:${user?.id}:${documentType}:${document?.id || `new-${initialJobId || initialOpportunityId || ''}`}`
  const [draftNotice,setDraftNotice] = useState('')
  const restoreDraft = () => {
    try {
      const draft = JSON.parse(localStorage.getItem(draftKey) || 'null')
      if (!draft) { setDraftNotice('No saved draft on this device.'); return }
      if (document && draft.updated_at !== document.updated_at && !window.confirm('This document changed after the draft was saved. Restore the saved draft?')) return
      setFormData(draft.formData); setLineItems(draft.lineItems); setDiscountAmount(draft.discountAmount)
      setDraftNotice('Saved draft restored. Review before submitting.')
    } catch { setDraftNotice('Unable to restore this draft.') }
  }
  const initialJob = useQuery({queryKey:['billing-initial-job',initialJobId],enabled:!!initialJobId && !document,queryFn:async()=>(await api.get<any>(`/api/jobs/${initialJobId}/`)).data})
  useEffect(()=>{
    if (initialJob.data?.associated_contact && !associationCustomerSeeded.current && !formData.customer) {
      associationCustomerSeeded.current=true
      const contact=initialJob.data.associated_contact
      setFormData(prev=>({...prev,customer:typeof contact==='object'?contact.id:contact}))
    }
  },[initialJob.data,formData.customer])

  /**
   * Keep the due date in step with the payment terms. It only follows while it
   * still matches the terms it came from — a date the user typed themselves is
   * left exactly where they put it.
   */
  const withDerivedDueDate = <T extends { document_date: string; due_date: string; payment_terms: string }>(
    prev: T,
    next: T
  ): T => {
    const wasDerived = !prev.due_date || prev.due_date === dueDateFromTerms(prev.document_date, prev.payment_terms)
    if (!wasDerived) return next
    return { ...next, due_date: dueDateFromTerms(next.document_date, next.payment_terms) || prev.due_date }
  }

  // Fetch organization settings on mount
  useEffect(() => {
    const fetchOrgSettings = async () => {
      try {
        const response = await api.get<{
          target_overhead?: number | null
          target_profit?: number | null
          default_tax_rate?: number | null
        }>('/api/organizations/current/')
        setOrganizationSettings({
          target_overhead: response.data.target_overhead || null,
          target_profit: response.data.target_profit || null,
          default_tax_rate: response.data.default_tax_rate || null
        })
        
        // Set default percentages if not already set
        if (!formData.overhead_percentage && response.data.target_overhead) {
          setFormData(prev => ({
            ...prev,
            overhead_percentage: response.data.target_overhead || null
          }))
        }
        if (!formData.profit_percentage && response.data.target_profit) {
          setFormData(prev => ({
            ...prev,
            profit_percentage: response.data.target_profit || null
          }))
        }
      } catch (error) {
        console.error('Failed to fetch organization settings:', error)
      }
    }
    fetchOrgSettings()
  }, [])

  // Seed the boilerplate from the org's default template for this document
  // type, so the warranty paragraph and terms are written once rather than
  // pasted onto every document. New documents only, empty fields only.
  useEffect(() => {
    if (document) return
    let cancelled = false
    documentTemplatesApi
      .list(documentType)
      .then(response => {
        const template = (response.results || []).find(t => t.is_default && t.is_active)
        if (!template || cancelled) return
        setFormData(prev => withDerivedDueDate(prev, {
          ...prev,
          terms_and_conditions: prev.terms_and_conditions || template.terms_and_conditions || '',
          notes: prev.notes || template.default_notes || '',
          payment_terms: paymentTermsTouched.current
            ? prev.payment_terms
            : template.default_payment_terms || prev.payment_terms
        }))
      })
      .catch(error => {
        console.error('Failed to fetch document defaults:', error)
      })
    return () => { cancelled = true }
  }, [])

  // A job or opportunity handed over in the URL has to run the same customer
  // copy the pickers below do, and it can only run once that list has loaded.
  useEffect(() => {
    if (document || associationCustomerSeeded.current || formData.customer) return
    if (formData.association_type === 'job' && formData.job) {
      const selectedJob = jobs.find(j => j.id === formData.job)
      const rawContact = selectedJob?.associated_contact as Contact | number | undefined
      if (rawContact != null) {
        associationCustomerSeeded.current = true
        updateField('customer', typeof rawContact === 'object' ? rawContact.id : rawContact)
      }
    } else if (formData.association_type === 'opportunity' && formData.opportunity) {
      const selectedOpp = opportunities.find(o => o.id === formData.opportunity)
      if (selectedOpp?.contact) {
        associationCustomerSeeded.current = true
        updateField('customer', selectedOpp.contact)
      }
    }
  }, [jobs, opportunities, formData.association_type, formData.job, formData.opportunity, formData.customer])

  // Calculate totals whenever line items, overhead/profit settings, or discount change
  useEffect(() => {
    calculateTotals()
  }, [lineItems, formData.include_overhead, formData.overhead_percentage,
      formData.include_profit, formData.profit_percentage, discountAmount,
      organizationSettings, taxRates])

  /**
   * Rate (0-1) applied to a taxable line that names no TaxRate of its own.
   * Mirrors BaseInvoiceDocument.default_tax_rate(): a configured TaxRate wins,
   * Billing Settings' percentage is the fallback. The form used to tax with the
   * settings percentage while the server only ever applied a TaxRate row, so
   * the total on screen was not the total that saved.
   */
  const resolveDefaultTaxRate = () => {
    const active = taxRates.find(rate => rate.is_active)
    if (active) return Number(active.rate) || 0
    return organizationSettings.default_tax_rate ? organizationSettings.default_tax_rate / 100 : 0
  }

  const calculateTotals = () => {
    let subtotal = 0
    let taxAmount = 0
    const defaultRate = resolveDefaultTaxRate()

    lineItems.forEach(item => {
      // Ensure numeric values for calculations
      const quantity = typeof item.quantity === 'string' ? parseFloat(item.quantity) : item.quantity
      const unitPrice = typeof item.unit_price === 'string' ? parseFloat(item.unit_price) : item.unit_price
      const itemTotal = quantity * unitPrice
      subtotal += itemTotal

      if (item.is_taxable) {
        const named = item.tax_rate ? taxRates.find(rate => rate.id === item.tax_rate) : undefined
        const rate = named ? Number(named.rate) || 0 : defaultRate
        const taxablePct = typeof item.taxable_pct === 'string' ? parseFloat(item.taxable_pct) : (item.taxable_pct || 1)
        taxAmount += itemTotal * taxablePct * rate
      }
    })

    // Calculate overhead if included
    let overheadAmount = 0
    if (formData.include_overhead && formData.overhead_percentage) {
      overheadAmount = subtotal * (formData.overhead_percentage / 100)
    }

    // Calculate profit if included
    let profitAmount = 0
    if (formData.include_profit && formData.profit_percentage) {
      profitAmount = subtotal * (formData.profit_percentage / 100)
    }

    const totalAmount = subtotal + overheadAmount + profitAmount + taxAmount - discountAmount

    setTotals({
      subtotal,
      overhead_amount: overheadAmount,
      profit_amount: profitAmount,
      tax_amount: taxAmount,
      discount_amount: discountAmount,
      total_amount: totalAmount
    })
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!lockFinancialFields && !formData.customer) {
      newErrors.customer = 'Customer is required'
    }

    if (!formData.document_date) {
      newErrors.document_date = 'Document date is required'
    }

    if (!lockFinancialFields) {
      if (lineItems.length === 0) {
        newErrors.lineItems = 'At least one line item is required'
      }

      lineItems.forEach((item, index) => {
        if (!item.description) {
          newErrors[`lineItem_${index}_description`] = 'Description is required'
        }
        const quantity = typeof item.quantity === 'string' ? parseFloat(item.quantity) : item.quantity
        const unitPrice = typeof item.unit_price === 'string' ? parseFloat(item.unit_price) : item.unit_price
        
        if (quantity <= 0) {
          newErrors[`lineItem_${index}_quantity`] = 'Quantity must be positive'
        }
        if (unitPrice < 0) {
          newErrors[`lineItem_${index}_price`] = 'Price cannot be negative'
        }
      })
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    console.log('handleSubmit called')
    console.log('Form data:', formData)
    console.log('Line items:', lineItems)

    if (!validateForm()) {
      console.log('Validation failed, errors:', errors)
      // Scroll to top to show error messages
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    setIsSaving(true)
    try {
      // Clean up the data - don't send association_type to backend
      const { association_type, ...cleanFormData } = formData

      // A backdated document derives a due date that has already passed, and
      // the create endpoint refuses any past due date. Send nothing and the
      // server derives the very same date from the very same terms; a past date
      // the user typed themselves is still sent, and still refused.
      const derivedDueDate = dueDateFromTerms(cleanFormData.document_date, cleanFormData.payment_terms)
      if (!document && cleanFormData.due_date === derivedDueDate && derivedDueDate < format(new Date(), 'yyyy-MM-dd')) {
        cleanFormData.due_date = ''
      }

      const data = {
        ...cleanFormData,
        ...totals,
        line_items: lineItems.map((item, index) => ({
          ...item,
          sort_order: index,
          // Re-index sub-items and strip the read-only `id` field so the
          // backend always recreates them fresh (the update flow deletes
          // and re-creates all line items).
          sub_items: (item.sub_items ?? []).map((sub, subIndex) => ({
            description: sub.description,
            quantity: sub.quantity,
            unit_price: sub.unit_price,
            item_type: sub.item_type,
            total: sub.total,
            sort_order: subIndex,
          })),
        }))
      }

      console.log('Submitting data:', data)
      await onSubmit(data)
      localStorage.removeItem(draftKey)
    } catch (error) {
      const data = (error as any).response?.data || (error as any).responseData || {detail:'Unable to save. Please retry.'}
      setErrors(Object.fromEntries(Object.entries(data).map(([key,value])=>[key,typeof value==='string'?value:JSON.stringify(value)])))
    } finally {
      setIsSaving(false)
    }
  }

  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error for this field
    setErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors[field]
      return newErrors
    })
  }

  const financialDisabled = isLoading || isSaving || lockFinancialFields

  return (
    <Box>
      <Stack spacing={3}>
        <Stack direction="row" sx={{gap:1,flexWrap:'wrap'}}>
          <Button variant="outlined" onClick={()=>{try{localStorage.setItem(draftKey,JSON.stringify({formData,lineItems,discountAmount,updated_at:document?.updated_at}));setDraftNotice('Draft saved on this device.')}catch{setDraftNotice('Unable to save draft on this device.')}}}>Save draft on this device</Button>
          <Button variant="plain" onClick={restoreDraft}>Restore saved draft</Button>
        </Stack>
        {draftNotice && <Alert>{draftNotice}</Alert>}
        {/* Validation Errors Summary */}
        {Object.keys(errors).length > 0 && (
          <Alert color="danger">
            <Typography level="title-sm" sx={{ mb: 1 }}>Please fix the following errors:</Typography>
            <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
              {Object.entries(errors).map(([key, message]) => (
                <li key={key}>{message}</li>
              ))}
            </ul>
          </Alert>
        )}

        {lockFinancialFields && (
          <Alert color="warning">
            <Typography level="body-sm" sx={{ color: 'inherit' }}>
              This invoice is paid. You can update the job association, notes, terms, and dates —
              customer, line items, and amounts are locked. Entered something incorrectly? Remove
              the payment from the invoice's Payments tab first: the invoice reverts to unpaid and
              unlocks, and the payment can be re-recorded afterward.
            </Typography>
          </Alert>
        )}

        {/* Header Information */}
        <Card>
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 3 }}>
              {documentType === 'invoice' ? 'Invoice' : 'Estimate'} Details
            </Typography>

            <Grid container spacing={3}>
              <Grid xs={12} md={6}>
                <BillingRecordPicker label="Customer" endpoint="/api/crm/contacts/" value={Number(formData.customer)||null} onChange={row=>updateField('customer',row?.id||null)} disabled={lockFinancialFields}/>
                {errors.customer && <Typography color="danger">{errors.customer}</Typography>}
              </Grid>

              <Grid xs={12}>
                <Box>
                  <Typography level="title-sm" sx={{mb:1}}>Association (Optional)</Typography>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={2} useFlexGap sx={{flexWrap:'wrap'}}>
                      <Button
                        variant={formData.association_type === 'none' ? 'solid' : 'outlined'}
                        onClick={() => {
                          setFormData(prev => ({ ...prev, association_type: 'none', job: null, opportunity: null }))
                        }}
                        size="sm"
                      >
                        No Association
                      </Button>
                      <Button
                        variant={formData.association_type === 'job' ? 'solid' : 'outlined'}
                        onClick={() => {
                          setFormData(prev => ({ ...prev, association_type: 'job', opportunity: null }))
                        }}
                        size="sm"
                      >
                        Associate with Job
                      </Button>
                      {/*
                        Invoices carry an opportunity too. The column has always
                        been on the shared document base; InvoiceSerializer just
                        did not list it, so this was gated to estimates and
                        pipeline attribution died at the invoice boundary.
                      */}
                      {(
                        <Button
                          variant={formData.association_type === 'opportunity' ? 'solid' : 'outlined'}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, association_type: 'opportunity', job: null }))
                          }}
                          size="sm"
                        >
                          Associate with Opportunity
                        </Button>
                      )}
                    </Stack>
                    {formData.association_type === 'job' && (
                      <BillingRecordPicker label="Job" endpoint="/api/jobs/?include_completed=true" value={Number(formData.job)||null} onChange={row=>{updateField('job',row?.id||null); if (row?.associated_contact) updateField('customer',typeof row.associated_contact==='object'?row.associated_contact.id:row.associated_contact)}}/>

                    )}
                    {formData.association_type === 'opportunity' && (
                      <Select
                        value={String(formData.opportunity || '')}
                        onChange={(_, value) => {
                          const opportunityId = value ? parseInt(value) : null
                          updateField('opportunity', opportunityId)
                          // Auto-fill customer based on opportunity
                          if (opportunityId) {
                            const selectedOpp = opportunities.find(o => o.id === opportunityId)
                            if (selectedOpp && selectedOpp.contact) {
                              updateField('customer', selectedOpp.contact)
                            }
                          }
                        }}
                        placeholder="Select an opportunity"
                      >
                        {opportunities.map(opp => (
                          <Option key={opp.id} value={String(opp.id)}>
                            {opp.name} - {opp.contact_name || `Contact #${opp.contact}`}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Stack>
                </Box>
              </Grid>

              <Grid xs={12} md={4}>
                <FormControl error={!!errors.document_date}>
                  <FormLabel>Document Date</FormLabel>
                  <Input
                    type="date"
                    value={formData.document_date}
                    onChange={(e) => {
                      const documentDate = e.target.value
                      setFormData(prev => withDerivedDueDate(prev, { ...prev, document_date: documentDate }))
                      setErrors(prev => {
                        const newErrors = { ...prev }
                        delete newErrors.document_date
                        return newErrors
                      })
                    }}
                  />
                  {errors.document_date && (
                    <Typography level="body-sm" color="danger">{errors.document_date}</Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid xs={12} md={4}>
                <FormControl>
                  <FormLabel>Due Date</FormLabel>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => updateField('due_date', e.target.value)}
                  />
                  {!!formData.due_date &&
                    formData.due_date === dueDateFromTerms(formData.document_date, formData.payment_terms) && (
                      <Typography level="body-xs" sx={{ mt: 0.5 }}>
                        Follows the payment terms — edit to override.
                      </Typography>
                    )}
                </FormControl>
              </Grid>

              {documentType === 'estimate' && (
                <Grid xs={12} md={4}>
                  <FormControl>
                    <FormLabel>Valid Until</FormLabel>
                    <Input
                      type="date"
                      value={formData.valid_until}
                      onChange={(e) => updateField('valid_until', e.target.value)}
                    />
                  </FormControl>
                </Grid>
              )}

              <Grid xs={12} md={6}>
                <FormControl>
                  <FormLabel>Payment Terms</FormLabel>
                  <Select
                    value={formData.payment_terms}
                    onChange={(_, value) => {
                      if (!value) return
                      paymentTermsTouched.current = true
                      setFormData(prev => withDerivedDueDate(prev, { ...prev, payment_terms: value }))
                    }}
                  >
                    {PAYMENT_TERMS.map(term => (
                      <Option key={term.value} value={term.value}>{term.label}</Option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {formData.payment_terms === 'custom' && (
                <Grid xs={12} md={6}>
                  <FormControl>
                    <FormLabel>Custom Payment Terms</FormLabel>
                    <Input
                      value={formData.custom_payment_terms}
                      onChange={(e) => updateField('custom_payment_terms', e.target.value)}
                      placeholder="Enter custom payment terms"
                    />
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card>
          <CardContent>
            {errors.lineItems && (
              <Alert color="danger" sx={{ mb: 2 }}>
                {errors.lineItems}
              </Alert>
            )}
            <LineItemsEditor
              lineItems={lineItems}
              onLineItemsChange={setLineItems}
              taxRates={taxRates}
              disabled={financialDisabled}
            />
          </CardContent>
        </Card>

        {/* Totals */}
        <Card>
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>Totals</Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>Subtotal:</Typography>
                <Typography fontWeight="lg">${totals.subtotal.toFixed(2)}</Typography>
              </Box>
              
              {/* Overhead */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Checkbox
                    checked={formData.include_overhead}
                    onChange={(e) => {
                      const checked = e.target.checked
                      setFormData(prev => ({
                        ...prev,
                        include_overhead: checked,
                        overhead_percentage: checked && !prev.overhead_percentage && organizationSettings.target_overhead 
                          ? organizationSettings.target_overhead 
                          : prev.overhead_percentage
                      }))
                    }}
                    disabled={financialDisabled}
                  />
                  <Typography>Include Overhead?</Typography>
                  {formData.include_overhead && (
                    <Input
                      type="number"
                      value={formData.overhead_percentage || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        overhead_percentage: parseFloat(e.target.value) || null
                      }))}
                      endDecorator="%"
                      slotProps={{
                        input: {
                          min: 0,
                          max: 100,
                          step: 0.01
                        }
                      }}
                      size="sm"
                      sx={{ width: 100 }}
                      disabled={financialDisabled}
                    />
                  )}
                </Box>
                {formData.include_overhead && (
                  <Typography fontWeight="lg">
                    ${totals.overhead_amount.toFixed(2)}
                  </Typography>
                )}
              </Box>
              
              {/* Profit */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Checkbox
                    checked={formData.include_profit}
                    onChange={(e) => {
                      const checked = e.target.checked
                      setFormData(prev => ({
                        ...prev,
                        include_profit: checked,
                        profit_percentage: checked && !prev.profit_percentage && organizationSettings.target_profit 
                          ? organizationSettings.target_profit 
                          : prev.profit_percentage
                      }))
                    }}
                    disabled={financialDisabled}
                  />
                  <Typography>Include Profit?</Typography>
                  {formData.include_profit && (
                    <Input
                      type="number"
                      value={formData.profit_percentage || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        profit_percentage: parseFloat(e.target.value) || null
                      }))}
                      endDecorator="%"
                      slotProps={{
                        input: {
                          min: 0,
                          max: 100,
                          step: 0.01
                        }
                      }}
                      size="sm"
                      sx={{ width: 100 }}
                      disabled={financialDisabled}
                    />
                  )}
                </Box>
                {formData.include_profit && (
                  <Typography fontWeight="lg">
                    ${totals.profit_amount.toFixed(2)}
                  </Typography>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>Tax:</Typography>
                <Typography fontWeight="lg">${totals.tax_amount.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>Discount:</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Input
                    type="number"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(parseFloat(e.target.value) || 0)}
                    startDecorator="$"
                    slotProps={{
                      input: {
                        min: 0,
                        step: 0.01
                      }
                    }}
                    size="sm"
                    sx={{ width: 120 }}
                    disabled={financialDisabled}
                  />
                </Box>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography level="title-md">Total:</Typography>
                <Typography level="title-lg" fontWeight="lg" color="primary">
                  ${totals.total_amount.toFixed(2)}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Additional Information */}
        <Accordion><AccordionSummary>Notes and terms</AccordionSummary><AccordionDetails>
            <Stack spacing={3}>
              <FormControl>
                <FormLabel>Notes (visible to customer)</FormLabel>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  minRows={3}
                  placeholder="Enter any notes for the customer"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Internal Notes (not visible to customer)</FormLabel>
                <Textarea
                  value={formData.internal_notes}
                  onChange={(e) => updateField('internal_notes', e.target.value)}
                  minRows={3}
                  placeholder="Enter internal notes"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Terms and Conditions</FormLabel>
                <Textarea
                  value={formData.terms_and_conditions}
                  onChange={(e) => updateField('terms_and_conditions', e.target.value)}
                  minRows={4}
                  placeholder="Enter terms and conditions"
                />
              </FormControl>
            </Stack>
          </AccordionDetails></Accordion>

        {/* Action Buttons */}
        <Box sx={{ position:'sticky',bottom:0,zIndex:2,bgcolor:'background.surface',p:2,borderTop:'1px solid',borderColor:'divider',display: 'flex',gap:1,flexWrap:'wrap',justifyContent: 'flex-end' }}>
          <Typography sx={{mr:'auto',alignSelf:'center'}}>Total ${totals.total_amount.toFixed(2)}</Typography>
          <Button
            variant="plain"
            color="neutral"
            startDecorator={<Cancel />}
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            startDecorator={isSaving ? <CircularProgress size="sm" /> : <Save />}
            onClick={handleSubmit}
            disabled={isLoading || isSaving}
          >
            {isSaving ? 'Saving...' : (document ? 'Update' : 'Create')} {documentType === 'invoice' ? 'Invoice' : 'Estimate'}
          </Button>
        </Box>
      </Stack>
    </Box>
  )
}